import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container,
  Typography,
  Paper,
  Grid,
  Tabs,
  Tab,
  Box,
  TextField,
  Button,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Slider,
  Stack,
  Autocomplete,
  IconButton,
  Tooltip,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BalanceIcon from '@mui/icons-material/Balance';
import GavelIcon from '@mui/icons-material/Gavel';
import InventoryIcon from '@mui/icons-material/Inventory';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SearchIcon from '@mui/icons-material/Search';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Chart, registerables, ChartData, ChartOptions } from 'chart.js';
import { Pie, Line } from 'react-chartjs-2';

import { 
  ImportExportAnalysis as ImportExportData, 
  TimeSeriesData, 
  importExportService,
  ExportData,
  ImportData
} from '../services/importExportService';
import MarketOpportunities from '../components/market/MarketOpportunities';
import { formatCurrency, formatNumber } from '../utils/formatters';

// Register chart.js components
Chart.register(...registerables);

interface CountryOption {
  label: string;
  value: string;
}

interface CommodityOption {
  label: string;
  value: string;
}

// Unit conversion multipliers
const UNITS = {
  KG: 1,
  TONNES: 0.001,
  KSH: 1
};

// Available time periods
const TIME_PERIODS = ['yearly', 'quarterly', 'monthly'] as const;
type TimePeriod = typeof TIME_PERIODS[number];

const ImportExportAnalysis: React.FC = () => {
  // Tab state
  const [tabValue, setTabValue] = useState<number>(0);
  
  // Search states
  const [commodityQuery, setCommodityQuery] = useState<string>('tea');
  const [countryQuery, setCountryQuery] = useState<string>('kenya');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [yearRange, setYearRange] = useState<[number, number]>([
    new Date().getFullYear() - 5,
    new Date().getFullYear()
  ]);
  
  // Data visualization options
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('yearly');
  const [selectedUnit, setSelectedUnit] = useState<keyof typeof UNITS>('KG');
  const [visibleTimeRange, setVisibleTimeRange] = useState<[number, number]>([0, 10]); // Visible range indexes
  
  // Data states
  const [countryData, setCountryData] = useState<ImportExportData | null>(null);
  const [commodityData, setCommodityData] = useState<any | null>(null);
  const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Autocomplete options
  const countries: CountryOption[] = [
    { label: 'Kenya', value: 'kenya' },
    { label: 'Tanzania', value: 'tanzania' },
    { label: 'Uganda', value: 'uganda' },
    { label: 'Ethiopia', value: 'ethiopia' },
    { label: 'Rwanda', value: 'rwanda' },
    { label: 'United States', value: 'united states' },
    { label: 'China', value: 'china' },
    { label: 'European Union', value: 'european union' },
    { label: 'Japan', value: 'japan' },
    { label: 'South Africa', value: 'south africa' }
  ];
  
  const commodities: CommodityOption[] = [
    { label: 'Tea', value: 'tea' },
    { label: 'Coffee', value: 'coffee' },
    { label: 'Cut Flowers', value: 'cut flowers' },
    { label: 'Fresh Vegetables', value: 'fresh vegetables' },
    { label: 'Fresh Fruits', value: 'fresh fruits' },
    { label: 'Maize', value: 'maize' },
    { label: 'Wheat', value: 'wheat' },
    { label: 'Rice', value: 'rice' },
    { label: 'Beans', value: 'beans' },
    { label: 'Potatoes', value: 'potatoes' },
    { label: 'Sugarcane', value: 'sugarcane' },
    { label: 'Avocados', value: 'avocados' },
    { label: 'Mangoes', value: 'mangoes' },
    { label: 'Macadamia Nuts', value: 'macadamia nuts' },
    { label: 'Cashew Nuts', value: 'cashew nuts' },
    { label: 'Pineapples', value: 'pineapples' },
    { label: 'Sisal', value: 'sisal' },
    { label: 'Cotton', value: 'cotton' },
    { label: 'Pyrethrum', value: 'pyrethrum' },
    { label: 'Tobacco', value: 'tobacco' },
    { label: 'Dairy Products', value: 'dairy products' },
    { label: 'Meat Products', value: 'meat products' },
    { label: 'Fish Products', value: 'fish products' },
    { label: 'Leather Products', value: 'leather products' },
    { label: 'Titanium Ores', value: 'titanium ores' },
    { label: 'Petroleum Products', value: 'petroleum products' },
    { label: 'Chemicals', value: 'chemicals' },
    { label: 'Pharmaceuticals', value: 'pharmaceuticals' },
    { label: 'Textile Products', value: 'textile products' },
    { label: 'Cement', value: 'cement' },
    { label: 'Machinery', value: 'machinery' },
    { label: 'Vehicles', value: 'vehicles' },
    { label: 'Electrical Equipment', value: 'electrical equipment' }
  ];
  
  // Chart ref for rendering
  const chartRef = useRef<HTMLCanvasElement>(null);
  const [chartInstance, setChartInstance] = useState<any>(null);
  const [chartData, setChartData] = useState<any>(null);
  const [chartOptions, setChartOptions] = useState<any>(null);

  // Load initial data and generate available years
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        // Get list of available years from service
        const years = importExportService.getAvailableYears();
        setAvailableYears(years);
        
        // Load country data for the current year
        await loadCountryData(selectedYear);
        
        // Load commodity data for the initial commodity
        if (commodityQuery) {
          await loadCommodityData(commodityQuery, selectedYear);
        }
        
        // Load time series data
        await loadTimeSeriesData(yearRange[0], yearRange[1]);
        
        setLoading(false);
      } catch (err) {
        setError('Failed to load trade data. Please try again later.');
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [commodityQuery, countryQuery, yearRange, selectedYear]);

  // Update data when selected year changes
  useEffect(() => {
    if (tabValue === 0) {
      loadCountryData(selectedYear);
    } else if (tabValue === 1 && commodityQuery) {
      loadCommodityData(commodityQuery, selectedYear);
    }
  }, [selectedYear, tabValue, commodityQuery]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Handle search based on current tab and selected filters
  const handleSearch = () => {
    if (tabValue === 1 && commodityQuery) {
      loadCommodityData(commodityQuery, selectedYear);
      loadTimeSeriesData(yearRange[0], yearRange[1]);
    } else if (tabValue === 0 || tabValue === 2) {
      loadCountryData(selectedYear);
      loadTimeSeriesData(yearRange[0], yearRange[1]);
    }
  };

  // Handle time period change
  const handleTimePeriodChange = (
    event: React.MouseEvent<HTMLElement>,
    newTimePeriod: TimePeriod,
  ) => {
    if (newTimePeriod !== null) {
      setTimePeriod(newTimePeriod);
      // Reset visible range when changing time period
      setVisibleTimeRange([0, getTimeLabelsForPeriod(newTimePeriod).length > 10 ? 10 : getTimeLabelsForPeriod(newTimePeriod).length]);
    }
  };

  // Handle unit change
  const handleUnitChange = (event: any) => {
    setSelectedUnit(event.target.value as keyof typeof UNITS);
  };

  // Handle year range change
  const handleYearRangeChange = (event: Event, newValue: number | number[]) => {
    const rangeValue = newValue as [number, number];
    setYearRange(rangeValue);
    loadTimeSeriesData(rangeValue[0], rangeValue[1]);
  };

  // Handle navigation through time series data
  const handleTimeRangeBack = () => {
    if (visibleTimeRange[0] > 0) {
      const newStart = Math.max(0, visibleTimeRange[0] - 5);
      setVisibleTimeRange([newStart, newStart + 10]);
    }
  };

  const handleTimeRangeForward = () => {
    const maxIndex = getTimeLabelsForPeriod(timePeriod).length;
    if (visibleTimeRange[1] < maxIndex) {
      const newStart = Math.min(maxIndex - 10, visibleTimeRange[0] + 5);
      setVisibleTimeRange([newStart, Math.min(newStart + 10, maxIndex)]);
    }
  };

  // Helper function to get time labels based on period
  const getTimeLabelsForPeriod = (period: TimePeriod): string[] => {
    if (!timeSeriesData) return [];
    return timeSeriesData[period].labels;
  };

  // Get visible time series data based on selected range
  const getVisibleTimeSeriesData = useCallback((): { labels: string[], exports: number[], imports: number[] } => {
    if (!timeSeriesData) {
      return { labels: [], exports: [], imports: [] };
    }

    const data = timeSeriesData[timePeriod];
    const start = visibleTimeRange[0];
    const end = Math.min(visibleTimeRange[1], data.labels.length);

    return {
      labels: data.labels.slice(start, end),
      exports: data.exports.slice(start, end),
      imports: data.imports.slice(start, end)
    };
  }, [timeSeriesData, timePeriod, visibleTimeRange]);

  // Convert values based on selected unit
  const convertToSelectedUnit = useCallback((value: number): number => {
    if (selectedUnit === 'TONNES') {
      return value * UNITS.TONNES;
    }
    return value;
  }, [selectedUnit]);

  // Format values for display based on selected unit
  const formatValue = (value: number): string => {
    const convertedValue = convertToSelectedUnit(value);
    
    if (selectedUnit === 'KSH') {
      return formatCurrency(convertedValue);
    } else if (selectedUnit === 'TONNES') {
      return `${formatNumber(convertedValue)} tonnes`;
    } else {
      return `${formatNumber(convertedValue)} kg`;
    }
  };

  // Load country data
  const loadCountryData = async (year: number) => {
    setLoading(true);
    try {
      const data = await importExportService.getCountryTradeAnalysis(year);
      setCountryData(data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load country data. Please try again later.');
      setLoading(false);
    }
  };

  // Load commodity data
  const loadCommodityData = async (commodity: string, year: number) => {
    setLoading(true);
    try {
      const data = await importExportService.getCommodityTradeData(commodity, year);
      setCommodityData(data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load commodity data. Please try again later.');
      setLoading(false);
    }
  };

  // Load time series data
  const loadTimeSeriesData = async (startYear: number, endYear: number) => {
    setLoading(true);
    try {
      const data = await importExportService.getTimeSeriesData(startYear, endYear);
      setTimeSeriesData(data);
      
      // Reset visible range when loading new data
      const totalLabels = data[timePeriod].labels.length;
      setVisibleTimeRange([0, totalLabels > 10 ? 10 : totalLabels]);
      
      setLoading(false);
      renderCharts();
    } catch (err) {
      setError('Failed to load time series data. Please try again later.');
      setLoading(false);
    }
  };

  // Render charts
  const renderCharts = useCallback(() => {
    if (!timeSeriesData) return;
    
    // Get visible data
    const visibleData = getVisibleTimeSeriesData();
    
    // Apply unit conversion to data if needed
    const convertedExports = visibleData.exports.map(value => convertToSelectedUnit(value));
    const convertedImports = visibleData.imports.map(value => convertToSelectedUnit(value));
    
    const chartData = {
      labels: visibleData.labels,
      datasets: [
        {
          label: 'Exports',
          data: convertedExports,
          borderColor: 'rgba(75, 192, 192, 1)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          tension: 0.1
        },
        {
          label: 'Imports',
          data: convertedImports,
          borderColor: 'rgba(255, 99, 132, 1)',
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          tension: 0.1
        }
      ]
    };
    
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: selectedUnit === 'KSH' ? 'Value (KSH)' : `Quantity (${selectedUnit})`
          }
        },
        x: {
          title: {
            display: true,
            text: `Time Period (${timePeriod})`
          }
        }
      }
    };
    
    setChartData(chartData);
    setChartOptions(chartOptions);
  }, [timeSeriesData, timePeriod, selectedUnit, visibleTimeRange, convertToSelectedUnit, getVisibleTimeSeriesData]);

  // Effect to render charts when data or visualization options change
  useEffect(() => {
    if (timeSeriesData) {
      renderCharts();
    }
  }, [timeSeriesData, renderCharts]);

  // Calculate min and max years with fallback to prevent errors
  const minYear = availableYears.length > 0 ? Math.min(...availableYears) : yearRange[0];
  const maxYear = availableYears.length > 0 ? Math.max(...availableYears) : yearRange[1];

  // Render pie chart for top export destinations
  const renderExportsPieChart = () => {
    if (!commodityData || !commodityData.exports || commodityData.exports.length === 0) {
      return <Typography variant="body2" color="textSecondary">No export data available</Typography>;
    }

    // Get top 5 export destinations plus "Others"
    const topExports = commodityData.exports.slice(0, 5);
    const totalTopExportValue = topExports.reduce((sum: number, item: ExportData) => sum + item.value, 0);
    const otherExportsValue = commodityData.exports.slice(5).reduce((sum: number, item: ExportData) => sum + item.value, 0);
    
    // Prepare chart data
    const destinations = [...topExports.map((item: ExportData) => item.destination)];
    if (otherExportsValue > 0) destinations.push('Others');
    
    const values = [...topExports.map((item: ExportData) => item.value)];
    if (otherExportsValue > 0) values.push(otherExportsValue);
    
    // Chart colors
    const backgroundColors = [
      'rgba(75, 192, 192, 0.7)',
      'rgba(54, 162, 235, 0.7)',
      'rgba(153, 102, 255, 0.7)',
      'rgba(255, 159, 64, 0.7)',
      'rgba(255, 99, 132, 0.7)',
      'rgba(201, 203, 207, 0.7)'
    ];
    
    const pieData: ChartData<'pie'> = {
      labels: destinations,
      datasets: [{
        data: values,
        backgroundColor: backgroundColors.slice(0, destinations.length),
        borderWidth: 1,
        borderColor: '#ffffff'
      }]
    };
    
    const options: ChartOptions<'pie'> = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right',
          labels: {
            boxWidth: 12,
            font: {
              size: 10
            }
          }
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = context.raw as number;
              const totalValue = values.reduce((a: number, b: number) => a + b, 0);
              const percentage = ((value / totalValue) * 100).toFixed(1);
              return `${context.label}: ${formatValue(value)} (${percentage}%)`;
            }
          }
        }
      }
    };
    
    return <Pie data={pieData} options={options} />;
  };
  
  // Render pie chart for top import sources
  const renderImportsPieChart = () => {
    if (!commodityData || !commodityData.imports || commodityData.imports.length === 0) {
      return <Typography variant="body2" color="textSecondary">No import data available</Typography>;
    }

    // Get top 5 import sources plus "Others"
    const topImports = commodityData.imports.slice(0, 5);
    const totalTopImportValue = topImports.reduce((sum: number, item: ImportData) => sum + item.value, 0);
    const otherImportsValue = commodityData.imports.slice(5).reduce((sum: number, item: ImportData) => sum + item.value, 0);
    
    // Prepare chart data
    const sources = [...topImports.map((item: ImportData) => item.source)];
    if (otherImportsValue > 0) sources.push('Others');
    
    const values = [...topImports.map((item: ImportData) => item.value)];
    if (otherImportsValue > 0) values.push(otherImportsValue);
    
    // Chart colors
    const backgroundColors = [
      'rgba(255, 99, 132, 0.7)',
      'rgba(255, 159, 64, 0.7)',
      'rgba(255, 205, 86, 0.7)',
      'rgba(75, 192, 192, 0.7)',
      'rgba(54, 162, 235, 0.7)',
      'rgba(201, 203, 207, 0.7)'
    ];
    
    const pieData: ChartData<'pie'> = {
      labels: sources,
      datasets: [{
        data: values,
        backgroundColor: backgroundColors.slice(0, sources.length),
        borderWidth: 1,
        borderColor: '#ffffff'
      }]
    };
    
    const options: ChartOptions<'pie'> = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right',
          labels: {
            boxWidth: 12,
            font: {
              size: 10
            }
          }
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = context.raw as number;
              const totalValue = values.reduce((a: number, b: number) => a + b, 0);
              const percentage = ((value / totalValue) * 100).toFixed(1);
              return `${context.label}: ${formatValue(value)} (${percentage}%)`;
            }
          }
        }
      }
    };
    
    return <Pie data={pieData} options={options} />;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Import/Export Data Analysis
      </Typography>
      
      <Paper sx={{ p: 2, mb: 4 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab icon={<ShowChartIcon />} label="Trade Overview" />
          <Tab icon={<InventoryIcon />} label="Commodity Analysis" />
          <Tab icon={<GavelIcon />} label="Trade Regulations" />
          <Tab icon={<TrendingUpIcon />} label="Market Opportunities" />
        </Tabs>
      </Paper>
      
      {/* Search Filters */}
      {tabValue !== 3 && (
      <Paper sx={{ p: 2, mb: 4 }}>
        <Grid container spacing={2} alignItems="center">
          {tabValue === 0 && (
            <>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle1">Country Overview</Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="year-select-label">Year</InputLabel>
                  <Select
                    labelId="year-select-label"
                    value={selectedYear}
                    label="Year"
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                  >
                    {availableYears.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="textSecondary">
                  View overall import/export data for Kenya
                </Typography>
              </Grid>
            </>
          )}
          
          {tabValue === 1 && (
            <>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  options={commodities}
                  getOptionLabel={(option) => option.label}
                  value={commodities.find(c => c.value === commodityQuery) || null}
                  onChange={(_, newValue) => {
                    if (newValue) {
                      setCommodityQuery(newValue.value);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Commodity" variant="outlined" />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel id="year-select-label">Year</InputLabel>
                  <Select
                    labelId="year-select-label"
                    value={selectedYear}
                    label="Year"
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                  >
                    {availableYears.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                  fullWidth
                >
                  Search
                </Button>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="textSecondary">
                  Analyze import/export patterns for specific commodities
                </Typography>
              </Grid>
            </>
          )}
          
          {tabValue === 2 && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle1">
                  Trade Regulations Overview
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  View import/export regulations, documentation requirements, and compliance information for Kenya
                </Typography>
              </Grid>
            </>
          )}
        </Grid>
      </Paper>
      )}
      
      {/* Data Visualization Options */}
      {tabValue !== 2 && tabValue !== 3 && (
        <Paper sx={{ p: 2, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Data Visualization Options
          </Typography>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Time Period
              </Typography>
              <ToggleButtonGroup
                value={timePeriod}
                exclusive
                onChange={handleTimePeriodChange}
                aria-label="time period"
                size="small"
                fullWidth
              >
                <ToggleButton value="yearly" aria-label="yearly">
                  Yearly
                </ToggleButton>
                <ToggleButton value="quarterly" aria-label="quarterly">
                  Quarterly
                </ToggleButton>
                <ToggleButton value="monthly" aria-label="monthly">
                  Monthly
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Unit of Measurement
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel id="unit-select-label">Unit</InputLabel>
                <Select
                  labelId="unit-select-label"
                  value={selectedUnit}
                  label="Unit"
                  onChange={handleUnitChange}
                >
                  <MenuItem value="KG">Kilograms</MenuItem>
                  <MenuItem value="TONNES">Tonnes</MenuItem>
                  <MenuItem value="KSH">Value (KSH)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Year Range
              </Typography>
              <Slider
                value={yearRange}
                onChange={handleYearRangeChange}
                valueLabelDisplay="auto"
                min={minYear}
                max={maxYear}
                marks
                sx={{ mt: 2 }}
                disabled={availableYears.length === 0}
              />
            </Grid>
          </Grid>
        </Paper>
      )}
      
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Paper sx={{ p: 2, mb: 4, bgcolor: 'error.light' }}>
          <Typography color="error" variant="body1">
            {error}
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Tab Content */}
          <Box role="tabpanel" hidden={tabValue !== 0} sx={{ mb: 4 }}>
            {tabValue === 0 && countryData && (
              <Grid container spacing={3}>
                {/* Trade Overview */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardHeader title="Trade Balance Overview" />
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" color="textSecondary">
                            Total Exports
                          </Typography>
                          <Typography variant="h6" color="success.main">
                            {formatCurrency(countryData.totalExportValue)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Main markets: East Africa, Europe, Asia
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" color="textSecondary">
                            Total Imports
                          </Typography>
                          <Typography variant="h6" color="error.main">
                            {formatCurrency(countryData.totalImportValue)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Primary sources: China, India, UAE
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" color="textSecondary">
                            Trade Balance
                          </Typography>
                          <Typography 
                            variant="h6" 
                            color={countryData.tradeBalance >= 0 ? 'success.main' : 'error.main'}
                          >
                            {formatCurrency(countryData.tradeBalance)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {countryData.tradeBalance >= 0 ? 'Trade surplus' : 'Trade deficit'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" color="textSecondary">
                            YoY Growth
                          </Typography>
                          <Typography 
                            variant="h6" 
                            color={countryData.yearOnYearGrowth >= 0 ? 'success.main' : 'error.main'}
                          >
                            {countryData.yearOnYearGrowth.toFixed(1)}%
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {countryData.yearOnYearGrowth >= 3 ? 'Strong growth' : countryData.yearOnYearGrowth >= 0 ? 'Moderate growth' : 'Declining'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
                
                {/* Top Exports */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardHeader 
                      title="Top Exports" 
                      subheader="Key export commodities by value"
                    />
                    <CardContent>
                      {countryData.topExports.slice(0, 3).map((exportItem, index) => (
                        <Box key={index} mb={index !== 2 ? 2 : 0}>
                          <Grid container spacing={1}>
                            <Grid item xs={8}>
                              <Typography variant="body2">
                                {exportItem.commodity} to {exportItem.destination}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                Market share: {index === 0 ? 25 : index === 1 ? 18 : 12}% of exports
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="body2" align="right">
                                {formatValue(exportItem.value)}
                              </Typography>
                              <Typography variant="caption" color="success.main" align="right" display="block">
                                ↑ {index === 0 ? 8.2 : index === 1 ? 5.7 : 4.3}% YoY
                              </Typography>
                            </Grid>
                          </Grid>
                          {index !== 2 && <Divider sx={{ mt: 1 }} />}
                        </Box>
                      ))}
                      <Box mt={2}>
                        <Typography variant="caption" color="textSecondary">
                          Other major exports: Chemicals, Textiles, Machinery
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                {/* Trade Trends Chart */}
                <Grid item xs={12}>
                  <Card>
                    <CardHeader 
                      title={`Trade Trends (${timePeriod})`}
                      action={
                        <Box display="flex" alignItems="center">
                          <Tooltip title="View previous data">
                            <span>
                              <IconButton 
                                onClick={handleTimeRangeBack}
                                disabled={visibleTimeRange[0] <= 0}
                              >
                                <NavigateBeforeIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="View more recent data">
                            <span>
                              <IconButton 
                                onClick={handleTimeRangeForward}
                                disabled={visibleTimeRange[1] >= getTimeLabelsForPeriod(timePeriod).length}
                              >
                                <NavigateNextIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      }
                    />
                    <CardContent>
                      <Box height={400}>
                        {chartData && chartOptions ? (
                          <Line data={chartData} options={chartOptions} />
                        ) : (
                          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                            <Typography color="textSecondary">No chart data available</Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </Box>
          
          <Box role="tabpanel" hidden={tabValue !== 1} sx={{ mb: 4 }}>
            {tabValue === 1 && commodityData && (
              <Grid container spacing={3}>
                {/* Commodity Overview */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardHeader title={`${commodityQuery.charAt(0).toUpperCase() + commodityQuery.slice(1)} Trade Analysis`} />
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" color="textSecondary">
                            Total Exports
                          </Typography>
                          <Typography variant="h6" color="success.main">
                            {formatValue(commodityData.analysis.totalExportValue)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Key markets: EU, China, Middle East
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" color="textSecondary">
                            Total Imports
                          </Typography>
                          <Typography variant="h6" color="error.main">
                            {formatValue(commodityData.analysis.totalImportValue)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Main sources: Tanzania, Ethiopia, Uganda
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }} />
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" color="textSecondary">
                            Trade Balance
                          </Typography>
                          <Typography 
                            variant="h6" 
                            color={commodityData.analysis.tradeBalance >= 0 ? 'success.main' : 'error.main'}
                          >
                            {formatValue(commodityData.analysis.tradeBalance)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {commodityData.analysis.tradeBalance >= 0 ? 
                              `Net ${commodityQuery} exporter` : 
                              `Net ${commodityQuery} importer`}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="subtitle2" color="textSecondary">
                            YoY Change
                          </Typography>
                          <Typography 
                            variant="h6" 
                            color={commodityData.analysis.yearOnYearChange >= 0 ? 'success.main' : 'error.main'}
                          >
                            {commodityData.analysis.yearOnYearChange.toFixed(1)}%
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {commodityData.analysis.yearOnYearChange >= 5 ? 'Rapidly growing' : 
                              commodityData.analysis.yearOnYearChange >= 0 ? 'Steady growth' : 'Contracting market'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} mt={1}>
                          <Typography variant="caption" color="textSecondary">
                            Market insights: {commodityQuery === 'tea' ? 
                              'Kenya remains a top global exporter with premium quality varieties' : 
                              commodityQuery === 'coffee' ? 
                              'Growing specialty coffee market with increasing export value' :
                              `Growing demand for Kenyan ${commodityQuery} in international markets`}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
                
                {/* Top Export Destinations */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardHeader 
                      title="Top Export Destinations" 
                      subheader={`Major markets for ${commodityQuery}`}
                    />
                    <CardContent>
                      {commodityData.exports.slice(0, 3).map((exportItem: ExportData, index: number) => (
                        <Box key={index} mb={index !== 2 ? 2 : 0}>
                          <Grid container spacing={1}>
                            <Grid item xs={8}>
                              <Typography variant="body2">
                                {exportItem.destination}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {index === 0 ? 'Primary market' : index === 1 ? 'Growing market' : 'Emerging market'} 
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography variant="body2" align="right">
                                {formatValue(exportItem.quantity)}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                color={index === 0 ? 'success.main' : 'info.main'} 
                                align="right" 
                                display="block"
                              >
                                {index === 0 ? '↑' : index === 1 ? '→' : '↗'} {index === 0 ? 9.4 : index === 1 ? 6.2 : 3.8}% YoY
                              </Typography>
                            </Grid>
                          </Grid>
                          {index !== 2 && <Divider sx={{ mt: 1 }} />}
                        </Box>
                      ))}
                      <Box mt={2}>
                        <Typography variant="caption" color="textSecondary">
                          Trade regulations: {commodityQuery === 'tea' ? 
                            'Subject to quality certification and phytosanitary requirements' : 
                            commodityQuery === 'coffee' ? 
                            'Must comply with International Coffee Organization standards' :
                            'May require export permits and quality inspection before shipping'}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                {/* Commodity Trade Trends Chart */}
                <Grid item xs={12}>
                  <Card>
                    <CardHeader 
                      title={`${commodityQuery.charAt(0).toUpperCase() + commodityQuery.slice(1)} Trade Trends (${timePeriod})`}
                      action={
                        <Box display="flex" alignItems="center">
                          <Tooltip title="View previous data">
                            <span>
                              <IconButton 
                                onClick={handleTimeRangeBack}
                                disabled={visibleTimeRange[0] <= 0}
                              >
                                <NavigateBeforeIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="View more recent data">
                            <span>
                              <IconButton 
                                onClick={handleTimeRangeForward}
                                disabled={visibleTimeRange[1] >= getTimeLabelsForPeriod(timePeriod).length}
                              >
                                <NavigateNextIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      }
                    />
                    <CardContent>
                      <Box height={400}>
                        {chartData && chartOptions ? (
                          <Line data={chartData} options={chartOptions} />
                        ) : (
                          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                            <Typography color="textSecondary">No chart data available</Typography>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                {/* Pie Charts for Export Destinations and Import Sources */}
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardHeader 
                      title="Top Export Destinations" 
                      subheader="Share of exports by country"
                    />
                    <CardContent>
                      <Box height={300}>
                        {renderExportsPieChart()}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card>
                    <CardHeader 
                      title="Top Import Sources" 
                      subheader="Share of imports by country"
                    />
                    <CardContent>
                      <Box height={300}>
                        {renderImportsPieChart()}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </Box>
          
          <Box role="tabpanel" hidden={tabValue !== 2} sx={{ mb: 4 }}>
            {tabValue === 2 && countryData && (
              <Grid container spacing={3}>
                {/* Trade Regulations */}
                <Grid item xs={12}>
                  <Card>
                    <CardHeader 
                      title="Import/Export Regulations" 
                      subheader="Key trading requirements and documentation"
                    />
                    <CardContent>
                      <Grid container spacing={3}>
                        {countryData.regulations.map((regulation, index) => (
                          <Grid item xs={12} key={index}>
                            <Card variant="outlined">
                              <CardContent>
                                <Typography variant="h6" gutterBottom>
                                  {regulation.category}
                                </Typography>
                                <Typography variant="body2" paragraph>
                                  {regulation.description}
                                </Typography>
                                <Typography variant="subtitle2" gutterBottom>
                                  Required Documents:
                                </Typography>
                                <ul>
                                  {regulation.documentRequired.map((doc, i) => (
                                    <li key={i}>
                                      <Typography variant="body2">{doc}</Typography>
                                    </li>
                                  ))}
                                </ul>
                                <Box mt={2}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Compliance Requirements:
                                  </Typography>
                                  <Typography variant="body2">
                                    {regulation.category.includes('Import') 
                                      ? 'Imports must comply with Kenyan Bureau of Standards specifications and may be subject to import duties and taxes as per the East African Community Customs Management Act.' 
                                      : 'Exports must meet quality standards, phytosanitary requirements, and may require certificates of origin to qualify for preferential tariff treatment under trade agreements.'}
                                  </Typography>
                                </Box>
                                <Box mt={2}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Relevant Authorities:
                                  </Typography>
                                  <Typography variant="body2">
                                    {regulation.category.includes('Import') 
                                      ? 'Kenya Revenue Authority (KRA), Kenya Bureau of Standards (KEBS), Kenya Plant Health Inspectorate Service (KEPHIS)' 
                                      : 'Kenya Export Promotion and Branding Agency (KEPROBA), Agriculture and Food Authority (AFA), Kenya Plant Health Inspectorate Service (KEPHIS)'}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" color="textSecondary">
                                  Last Updated: {regulation.lastUpdated}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}
          </Box>

          <Box role="tabpanel" hidden={tabValue !== 3} sx={{ mb: 4 }}>
            {tabValue === 3 && <MarketOpportunities />}
          </Box>
        </>
      )}
      
      {/* Data Source */}
      {!loading && !error && (countryData || commodityData) && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="caption" color="textSecondary">
            Data Source: {countryData?.dataSource || 'Kenya National Bureau of Statistics'}
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default ImportExportAnalysis;